import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteCustomerCategory, getAdminCustomerCategories } from 'apis/adminCustomerCategories';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const CustomerCategoriesList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? '' : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('customer-categories', queryParams, getAdminCustomerCategories);
    const deleteMutation = useAdminDeleteMutation('customer-categories', deleteCustomerCategory);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'Tải dữ liệu thất bại'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res?.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Tên loại đối tác / khách hàng',
            render: (row) => <strong>{row.translations?.[0]?.name || row.name || '—'}</strong>,
        },
        {
            header: 'Slug',
            render: (row) => <span style={{ color: '#9ca3af', fontSize: 13 }}>{row.slug || '—'}</span>,
        },
        { field: 'sortOrder', header: 'Thứ tự' },
        {
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Khóa'}
                </span>
            ),
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" title="Sửa" onClick={() => navigate(`/admin/customer-categories/edit/${row.id}`)}>
                        <FiEdit2 />
                    </button>
                    <button className="icon-btn icon-btn--delete" title="Xóa" onClick={() => setDeleteId(row.id)}>
                        <FiTrash2 />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Quản lý Loại Đối tác & Khách hàng"
                action={<Button onClick={() => navigate('/admin/customer-categories/create')}>+ Thêm Mới</Button>}
            />

            <div className="filters-bar">
                <div className="filter-item">
                    <Input placeholder="Tìm kiếm theo tên..." value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                </div>
                <div className="filter-item">
                    <Select
                        value={languageCodeFilter}
                        onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={languages.map((l) => ({ value: l.code, label: l.name }))}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={(e) => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Khóa' },
                        ]}
                    />
                </div>
            </div>

            <Table
                columns={columns}
                data={data.items || []}
                loading={isFetching}
                pagination={{
                    currentPage,
                    totalPages: Math.ceil((data.total || 0) / pageSize) || 1,
                    totalItems: data.total || 0,
                    onPageChange: (page) => setCurrentPage(page),
                }}
            />

            <ConfirmationDialog
                isOpen={!!deleteId}
                title="Xác nhận xóa"
                message="Bạn có chắc chắn muốn xóa loại đối tác / khách hàng này?"
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
            />
        </div>
    );
};

export default CustomerCategoriesList;
